import { Tune } from "@mui/icons-material";
import {
  Box,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type FilterMenuItem = {
  filter: string;
  translation: string;
  icon?: JSX.Element;
  count?: number;
};

type Props = {
  menuItemsTitle?: string;
  menuItemList?: FilterMenuItem[];
  selectedMenuItems?: string[];
  onFilterData?: (status: string[]) => void;
  secondaryMenuItemsTitle?: string;
  secondaryMenuItemList?: FilterMenuItem[];
  selectedSecondaryMenuItems?: string[];
  onSecondaryFilterData?: (orderTypes: string[]) => void;
  onResetFilters?: () => void;
  /** Optional; runs when the menu closes (backdrop, Escape, or after reset). */
  onFiltersMenuClose?: () => void;
  /** When true, shows a small dot on the icon to indicate that filters are selected */
  hasActiveFilters?: boolean;
};

function FilterMenuItems(props: {
  options: FilterMenuItem[];
  selected: string[] | undefined;
  onToggle: ((next: string[]) => void) | undefined;
  sectionTitle?: string;
}) {
  const { options, selected, onToggle, sectionTitle } = props;

  return (
    <>
      {sectionTitle && (
        <ListSubheader
          disableSticky
          sx={{ bgcolor: "background.paper", lineHeight: 2.25 }}
        >
          {sectionTitle}
        </ListSubheader>
      )}
      {options.map((item) => (
        <MenuItem
          key={item.filter}
          onClick={() => {
            if (selected && onToggle) {
              if (selected.indexOf(item.filter) === -1) {
                onToggle([...selected, item.filter]);
              } else {
                onToggle(selected.filter((el) => el !== item.filter));
              }
            }
          }}
          style={{
            minWidth: 150,
            backgroundColor: selected?.includes(item.filter)
              ? "rgb(219, 219, 219)"
              : undefined,
            margin: "5px 0px"
          }}
        >
          <Stack sx={{ flexDirection: "row", gap: 1, alignItems: "center" }}>
            {item.icon && item.icon}
            <Typography> {item.translation}</Typography>
            {item.count !== undefined && (
              <Typography variant="body2" color="text.secondary">
                ({item.count})
              </Typography>
            )}
          </Stack>
        </MenuItem>
      ))}
    </>
  );
}

/**
 * Renders a button with a filter icon that opens a menu of the provided filters.
 */
export function NavbarFilters(props: Props) {
  const {
    menuItemsTitle,
    menuItemList,
    selectedMenuItems,
    onFilterData,
    secondaryMenuItemList,
    selectedSecondaryMenuItems,
    onSecondaryFilterData,
    secondaryMenuItemsTitle,
    onResetFilters,
    onFiltersMenuClose,
    hasActiveFilters = false
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { t } = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <IconButton
          aria-label="Navbar filters menu"
          aria-controls="simple-menu"
          aria-haspopup="true"
          onClick={handleClick}
        >
          <Tune sx={{ color: "secondary.contrastText" }} />
        </IconButton>
        {hasActiveFilters && (
          <Box
            aria-label="Active filters"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: "primary.main"
            }}
          />
        )}
      </Box>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={() => {
          handleClose();
          onFiltersMenuClose?.();
        }}
        slotProps={{
          list: {
            dense: !!secondaryMenuItemList
          },
          paper: {
            // Disabling transition as it shifts the menu items
            sx: { transition: "none !important" }
          }
        }}
      >
        {menuItemList && menuItemList.length > 0 && (
          <FilterMenuItems
            options={menuItemList}
            selected={selectedMenuItems}
            onToggle={onFilterData}
            sectionTitle={menuItemsTitle}
          />
        )}
        {secondaryMenuItemList && secondaryMenuItemList.length > 0 && (
          <>
            <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
            <FilterMenuItems
              options={secondaryMenuItemList}
              selected={selectedSecondaryMenuItems}
              onToggle={onSecondaryFilterData}
              sectionTitle={secondaryMenuItemsTitle}
            />
          </>
        )}
        <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
        <MenuItem
          aria-label="Reset filters"
          key="reset filters"
          onClick={() => {
            if (onResetFilters) onResetFilters();
            handleClose();
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Box>{t("reset filters")}</Box>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
